import {FormattedMessage, useIntl} from "react-intl";
import {TitlePage} from "../shared";
import {isMobileDevice, setDarkMode} from "../../utils/utils";
import {useDispatch, useSelector} from "react-redux";
import users from "../users";
import functions from "../../utils/functionsFirebase";
import {useRef, useState} from "react";
import UseUserPreferences, {buttonsOptions} from "./hooks";
import './user-app-config.scss';
import {ScrollModal} from "../shared/scroll-modal";
import {ScrollModalApi} from "../shared/scroll-modal/view";
import ButtonV2 from "../shared/button-v2/button-v2";
import AppConfigSquadflix from "./app-config-squadflix/app-config-squadflix";
import AppShortcutsConfig from "./app-shortcuts-config/app-shortcuts-config";

type InputToggleProps = {
    label: React.ReactNode;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    checked: boolean;
    description?: string;
    disabled?: boolean;
    className?: string;
    customInputPadding?: string;
}

export const InputToggle = ({
                                onChange,
                                checked,
                                label,
                                description,
                                disabled,
                                className,
                                customInputPadding
                            }: InputToggleProps) => {
    const randomUid = useRef(Math.random().toString(36).substring(2, 15));

    return (<div className={className ?? "mb-3"}>
        <label style={{fontWeight: "bold"}} htmlFor={randomUid.current} className="form-label">
            {label}
        </label>
        {description && <div className="mb-2">
            <small className="form-text text-muted2">{description}</small>
        </div>}
        <div className={customInputPadding ?? (isMobileDevice() ? "ps-3" : "ps-5")}>
            <div className="form-check form-switch">
                <input disabled={disabled} checked={checked} onChange={e => {
                    onChange(e);
                }} className="form-check-input" type="checkbox" role="switch"
                       id={randomUid.current}/>
            </div>
        </div>
    </div>);
}

const UserAppConfig = () => {

    const intl = useIntl();
    const dispatch = useDispatch();
    const userConfig = useSelector(users.selectors.getUserConfiguration);
    const {userPreferences, updatePreference, loadingUpdate} = UseUserPreferences();
    const [editingPosition, setEditingPosition] = useState<number | null>(null);
    const editBtnModalRef = useRef<ScrollModalApi>(null);

    async function saveNewUserConfiguration(config) {
        const saveConfiguration = functions.httpsCallable('setUserConfiguration');
        const res = await saveConfiguration({configuration: config});
        dispatch(users.actions.loadedUserConfig(res.data));
    }

    async function toogleDarkMode(e) {
        e.target.disabled = true;
        await saveNewUserConfiguration({...userConfig, darkMode: !userConfig.darkMode});
        setDarkMode(!userConfig.darkMode);
        e.target.disabled = false;
    }

    async function toggleReceiveEmails(e) {
        e.target.disabled = true;
        await saveNewUserConfiguration({...userConfig, receiveEmails: !userConfig.receiveEmails});
        e.target.disabled = false;
    }

    async function togglePrivateAccount(e) {
        e.target.disabled = true;
        await updatePreference("privateAccount", !userPreferences.privateAccount);
        e.target.disabled = false;
    }

    async function toggleShowScrollbar(e) {
        e.target.disabled = true;
        await updatePreference("showScrollbar", !userPreferences.showScrollbar);
        e.target.disabled = false;
    }

    async function toggleHideSpoilers(e) {
        e.target.disabled = true;
        await updatePreference("hideSpoilers", !userPreferences.hideSpoilers);
        e.target.disabled = false;
    }

    async function toggleTvMode(e) {
        e.target.disabled = true;
        const nextTvMode = !userPreferences.tvMode;
        await updatePreference("tvMode", nextTvMode);
        if (nextTvMode) {
            setDarkMode(true);
        }
        e.target.disabled = false;
    }

    if (!userPreferences) {
        return null;
    }

    return (
        <div className="pb-3">
            <div>
                <TitlePage icon="fa-solid fa-gear" title="Preferencias"
                           leftClass="col-12" rightOptions={undefined} titleClass={undefined} rightClass={undefined}
                           disableScroll={undefined} hideSeparator={undefined} fixedHeight={undefined}
                           className={undefined} style={undefined}/>

                <InputToggle label={<><i className="fa-solid fa-tv pe-2"></i>Modo TV</>}
                             onChange={toggleTvMode}
                             checked={!!userPreferences.tvMode}
                             description="Activa la interfaz adaptada para televisores y control con mando a distancia (SquadFlix TV)."/>

                <InputToggle label={"Mostrar scrollbar"} onChange={toggleShowScrollbar}
                             checked={userPreferences.showScrollbar}
                             description="Muestra la barra lateral de scroll"/>
                             
                <InputToggle label={<FormattedMessage id="profile.app-config.dark-mode"/>} onChange={toogleDarkMode}
                             checked={userConfig.darkMode}/>

                <InputToggle label={"Ocultar Spoilers Series"} onChange={toggleHideSpoilers}
                             checked={userPreferences.hideSpoilers}/>

                <InputToggle label={<FormattedMessage id="profile.app-config.receive-emails"/>}
                             onChange={toggleReceiveEmails} checked={userConfig.receiveEmails}
                             description="Se recibirán notificaciones por correo electrónico. En ningún caso se enviará SPAM."/>

                <InputToggle label={"Cuenta privada"} onChange={togglePrivateAccount}
                             checked={userPreferences.privateAccount}
                             description="Solo la gente que te siga podrá ver tus publicaciones."/>

            </div>
            {!isMobileDevice() && <div className="pt-3 mb-3">
                <TitlePage icon="fa-solid fa-link" title="Accesos directos"
                           leftClass="col-12" rightOptions={undefined} titleClass={undefined} rightClass={undefined}
                           disableScroll={undefined} hideSeparator={undefined} fixedHeight={undefined}
                           className={undefined} style={undefined}/>
                <div className="mb-3">
                    <small className="form-text text-muted ">Modifica los accesos directos que quieras que aparezcan en
                        tu tarjeta de perfil (barra lateral izquierda)</small>
                </div>
                {!!userPreferences.shortcuts?.length &&
                    <AppShortcutsConfig shortcuts={userPreferences.shortcuts} onChange={newShortcuts => {
                        updatePreference("shortcuts", newShortcuts);
                    }}/>}
            </div>}
            {isMobileDevice() && <div className="pt-3 mb-3">
                <TitlePage icon="fa-solid fa-tablet-screen-button" title="Barra de botones"
                           leftClass="col-12" rightOptions={undefined} titleClass={undefined} rightClass={undefined}
                           disableScroll={undefined} hideSeparator={undefined} fixedHeight={undefined}
                           className={undefined} style={undefined}/>
                <div className="mb-3">
                    <small className="form-text text-muted ">Selecciona los botones que quieres que aparezcan en la
                        barra inferior de la aplicación en móvil.</small>
                </div>
                <div className="user-app-config__bar-buttons">
                    {userPreferences.barButtons.sort((a, b) => a.order - b.order).map(btn => {
                        const btnInfo = buttonsOptions.find(b => b.id === btn.id);
                        return <ButtonV2
                            size="small"
                            onClick={() => {
                                if (!btn.editable) return;
                                setEditingPosition(btn.order);
                                editBtnModalRef.current?.open();
                            }}
                            disabled={!btn.editable} className="user-app-config__bar-buttons-btn" key={btn.id}>
                            <div>
                                {btnInfo?.name ?? "Nada"}
                            </div>
                            <div>
                                {!btn.editable && <i className="fa-solid fa-lock ps-2"></i>}
                                {btn.editable && <i className="fa-solid fa-pencil ps-2"></i>}
                            </div>
                        </ButtonV2>;
                    })}
                </div>
                <ScrollModal ref={editBtnModalRef} header={<h2>
                    <i className="fa-solid fa-pencil pe-2"></i>{"Editar boton N" + (editingPosition ?? "")}
                </h2>}>
                    {editingPosition !== null && <>
                        {buttonsOptions.map(btnOption => {
                            const alreadyInUse = userPreferences.barButtons.find(b => b.id === btnOption.id);
                            const isCurrent = userPreferences.barButtons.find(b => b.order === editingPosition)?.id === btnOption.id;
                            return <ButtonV2 key={btnOption.id} disabled={alreadyInUse && !isCurrent}
                                             className={"user-app-config__bar-buttons-btn w-100 mb-2 mt-2"}
                                             active={isCurrent}
                                             size="small"
                                             onClick={async () => {
                                                 const newButtons = userPreferences.barButtons.map(b => {
                                                     if (b.order === editingPosition) {
                                                         return {...b, id: btnOption.id};
                                                     }
                                                     return b;
                                                 });
                                                 await updatePreference("barButtons", newButtons);
                                                 setEditingPosition(null);
                                                 editBtnModalRef.current?.close();
                                             }}>
                                <div>
                                    {btnOption.name}
                                </div>
                                <div>
                                    {isCurrent && <i className="fa-solid fa-check ps-2"></i>}
                                    {alreadyInUse && !isCurrent && <i className="fa-solid fa-lock ps-2"></i>}
                                </div>
                            </ButtonV2>;
                        })}
                    </>}
                </ScrollModal>
            </div>}
            <div className="pt-3 mb-3">
                <TitlePage icon="fa-solid fa-home" title="Pagina de inicio"
                           leftClass="col-12" rightOptions={undefined} titleClass={undefined} rightClass={undefined}
                           disableScroll={undefined} hideSeparator={undefined} fixedHeight={undefined}
                           className={undefined} style={undefined}/>
                <div className="mb-3">
                    <small className="form-text text-muted ">Selecciona la pantalla de inicio para cada vez que abras
                        SquadDisk.</small>
                </div>
                <div className="user-app-config__bar-buttons">
                    {buttonsOptions.filter(b => b.home).map(btnOption => {
                        const isCurrent = userPreferences.home === btnOption.id;
                        return <ButtonV2 key={btnOption.id}
                                         size="small"
                                         active={isCurrent}
                                         className="user-app-config__bar-buttons-btn"
                                         onClick={async () => {
                                             await updatePreference("home", btnOption.id);
                                         }}>
                            <div>
                                {btnOption.name}
                            </div>
                            <div>
                                {isCurrent && <i className="fa-solid fa-check ps-2"></i>}
                            </div>
                        </ButtonV2>;
                    })}
                </div>
            </div>
            <div className="pt-3 mb-3">
                <AppConfigSquadflix loadingUpdate={loadingUpdate} userPreferences={userPreferences}
                                    updatePreference={updatePreference}/>
            </div>
        </div>
    )
};

export default UserAppConfig;
